import {
  type AllKeys,
  type Compare,
  notEmpty,
  type OptionalRecord,
} from '@festina-life/core'
import cx from 'classnames'
import get from 'lodash-es/get'

import { CompareValues } from '../../structure/compareValues/CompareValues'
import type { Column } from '../sortableTable/createColumnDefiner'

export const mapColumnToCompare = <T extends OptionalRecord>({
  column: {
    heading,
    width,
    keyPath,
    alignment,
    onCellClick,
    sortable,
    sortOptions,
    filterMode,
    tdClassName,
    noWrap,
    getCellValue,
    getSortValue,
    getCellDisplay,
    getValueDisplay,
  },
  isDiffing,
}: {
  column: Column<T>
  isDiffing: boolean
}): Column<Compare<T>> => ({
  heading,
  width,
  keyPath,
  alignment,
  onCellClick: undefined, // done in the display
  sortable,
  sortOptions,
  filterMode:
    typeof filterMode === 'object'
      ? {
          type: filterMode.type,
          options: filterMode.options.map((option) => ({
            label: option.label,
            filter: (row) => {
              let shouldKeep = false
              if (row.current !== undefined) {
                shouldKeep = option.filter(row.current)
              }
              if (isDiffing && !shouldKeep && row.compare !== undefined) {
                shouldKeep = option.filter(row.compare)
              }
              return shouldKeep
            },
          })),
        }
      : filterMode,
  tdClassName: tdClassName
    ? (row) =>
        cx(
          row.current ? tdClassName(row.current) : undefined,
          isDiffing && row.compare ? tdClassName(row.compare) : undefined,
        )
    : undefined,
  noWrap,
  getCellValue: (row) =>
    [
      row.current === undefined ? undefined : getCellValue(row.current),
      row.compare === undefined || !isDiffing
        ? undefined
        : getCellValue(row.compare),
    ].filter(notEmpty),
  getSortValue: (row) =>
    [
      row.current === undefined ? undefined : getSortValue(row.current),
      row.compare === undefined || !isDiffing
        ? undefined
        : getSortValue(row.compare),
    ].filter(notEmpty),
  getCellDisplay: ({ current, compare }) => {
    const rowForMappedValue = (value: unknown) => {
      // Need to "undo" what `CompareValues` did to the row to recover the row.
      //
      // This assumes the `keyPath` handling here is implemented in the
      // same manner as in `CompareValues`. A test checks for this property.
      if (current !== undefined) {
        const currentValue = keyPath ? get(current, keyPath) : current
        if (currentValue === value) {
          return current
        }
      }

      if (compare !== undefined) {
        const compareValue = keyPath ? get(compare, keyPath) : compare
        if (compareValue === value) {
          return compare
        }
      }

      return undefined
    }

    return (
      <CompareValues
        current={current}
        formatter={(value) => {
          const row = rowForMappedValue(value)
          return row === undefined
            ? getValueDisplay(value) // diff calculates a new value not belonging to the row
            : getCellDisplay(row)
        }}
        formatterForCompare={(value) => {
          const row = rowForMappedValue(value)
          return row === undefined ? '' : `${getSortValue(row)}`
        }}
        currentOnClick={
          current === undefined || !onCellClick
            ? undefined
            : (event) => onCellClick(current, event)
        }
        prev={compare}
        prevOnClick={
          compare === undefined || !onCellClick
            ? undefined
            : (event) => onCellClick(compare, event)
        }
        align={alignment}
        isDiffing={isDiffing}
        keyPath={
          // Column type-erased the keyPath, but is otherwise still valid.
          // We know this because the formatter correctly receives the TypeForKey.
          keyPath as AllKeys<T>
        }
        extraTestInformationTitle={heading}
      />
    )
  },
  getValueDisplay, // defaults to the per-item `getCellDisplay`, which is what we need
})
